import { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react'
import { SupportedLanguages, SuppotedLocales, mapLocaleToAvailableLanguage, useIsoCountries } from '../i18n'
import { IntlProvider } from 'react-intl'

interface LocaleContextType {
  locale: SuppotedLocales
  lang: SupportedLanguages
  countryCode?: string
  countries: { name: string; code: string }[]
  setLocale: (locale: SuppotedLocales) => void
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined)

export function useLocalization() {
  const context = useContext(LocaleContext)
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider')
  }
  return context
}

interface LocalizationProviderProps {
  children: ReactNode
  initialLocale?: SuppotedLocales
  supportedLocales?: SuppotedLocales[]
  translations?: Record<SupportedLanguages, Record<string, string>>
}

const defaultSupportedLocales: SuppotedLocales[] = [SuppotedLocales.EN_GB, SuppotedLocales.SV_SE, SuppotedLocales.EL_GR]
function getInitialLocale(supportedLocales: SuppotedLocales[], providedLocale?: SuppotedLocales): SuppotedLocales {
  if (providedLocale && supportedLocales.includes(providedLocale)) {
    return providedLocale
  }

  const storedLocale =
    typeof localStorage !== 'undefined' ? (localStorage.getItem('userLocale') as SuppotedLocales | null) : null

  if (storedLocale && supportedLocales.includes(storedLocale)) {
    return storedLocale
  }

  const browserLocale =
    typeof navigator !== 'undefined' ? (navigator.language.split('-')[0] as SuppotedLocales) : SuppotedLocales.EN_GB

  return supportedLocales.includes(browserLocale) ? browserLocale : SuppotedLocales.EN_GB
}

export function LocalizationProvider({
  children,
  initialLocale,
  supportedLocales = defaultSupportedLocales,
  translations,
}: LocalizationProviderProps) {
  const { getCountries } = useIsoCountries()
  const [countries, setCountries] = useState<{ name: string; code: string }[]>([])
  const [locale, setLocale] = useState<SuppotedLocales>(getInitialLocale(supportedLocales, initialLocale))

  const handleLocaleChange = (newLocale: SuppotedLocales) => {
    if (supportedLocales.includes(newLocale)) {
      setLocale(newLocale)
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('userLocale', newLocale)
      }
    }
  }

  useEffect(() => {
    setCountries(getCountries(mapLocaleToAvailableLanguage(locale)))
  }, [getCountries, locale])

  const val = useMemo(
    () => ({
      locale,
      lang: mapLocaleToAvailableLanguage(locale),
      countryCode: locale.split('_')[1]?.toLowerCase(),
      countries,
      setLocale: handleLocaleChange,
    }),
    [locale, countries],
  )

  return (
    <LocaleContext.Provider value={val}>
      <IntlProvider locale={val.lang} messages={translations?.[val.lang]}>
        {children}
      </IntlProvider>
    </LocaleContext.Provider>
  )
}
